import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useScreenTour } from "@/hooks/useScreenTour";
import { PageHeader } from "@/components/PageHeader";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { EmptyFiltered } from "@/components/EmptyFiltered";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Download, FileSpreadsheet, AlertTriangle } from "lucide-react";

function SentidoBadge({ sentido }) {
  return sentido === "impo" ? (
    <Badge className="border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50">Impo</Badge>
  ) : (
    <Badge className="border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Expo</Badge>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const EMPTY_CAJA = { numero_caja: "", tipo: "", notas: "" };
const EMPTY_MOV = {
  fecha: new Date().toISOString().slice(0, 10), caja_numero: "", sentido: "impo",
  cliente_nombre: "", client_id: null, client_label: "", bodega: "", referencia: "",
  carta_porte: "", carta_porte_cruce: "", factura: "", tracto: "", operador: "", status: "", notas: "",
};
const EMPTY_MOV_FILTERS = { caja_numero: "", sentido: "all", fecha_desde: "", fecha_hasta: "", cliente_nombre: "" };

export default function Cajas() {
  const { can } = useAuth();
  useScreenTour("cajas");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(() => (searchParams.get("tab") === "movimientos" ? "movimientos" : "catalogo"));
  const setTab = (v) => {
    setTabState(v);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (v === "catalogo") p.delete("tab"); else p.set("tab", v); return p; });
  };

  // --- catálogo ---
  const [cajas, setCajas] = useState({ items: [], total: 0 });
  const [cajaSearch, setCajaSearch] = useState("");
  const [cajaPage, setCajaPage] = useState(0);
  const [cajaLoading, setCajaLoading] = useState(false);
  const [cajaOpen, setCajaOpen] = useState(false);
  const [cajaEditing, setCajaEditing] = useState(null);
  const [cajaForm, setCajaForm] = useState(EMPTY_CAJA);
  const [cajaSimilar, setCajaSimilar] = useState([]);
  const [cajaSaving, setCajaSaving] = useState(false);
  const limit = 50;

  const loadCajas = useCallback(async () => {
    setCajaLoading(true);
    try {
      const { data } = await api.get("/cajas", { params: { search: cajaSearch, skip: cajaPage * limit, limit } });
      setCajas(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCajaLoading(false);
    }
  }, [cajaSearch, cajaPage]);

  useEffect(() => {
    if (tab !== "catalogo") return;
    const t = setTimeout(loadCajas, 250);
    return () => clearTimeout(t);
  }, [loadCajas, tab]);

  const openCreateCaja = () => { setCajaEditing(null); setCajaForm(EMPTY_CAJA); setCajaSimilar([]); setCajaOpen(true); };
  const openEditCaja = (c) => { setCajaEditing(c); setCajaForm({ ...EMPTY_CAJA, ...c }); setCajaSimilar([]); setCajaOpen(true); };

  const saveCaja = async (force = false) => {
    setCajaSaving(true);
    try {
      if (cajaEditing) {
        await api.put(`/cajas/${cajaEditing.id}`, cajaForm);
        toast.success("Caja actualizada");
      } else {
        await api.post(`/cajas?force=${force}`, cajaForm);
        toast.success("Caja creada");
      }
      setCajaOpen(false);
      loadCajas();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "posible_duplicado") {
        setCajaSimilar(detail.similar || []);
        toast.warning(detail.message);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setCajaSaving(false);
    }
  };

  const toggleCajaActivo = async (c) => {
    try {
      await api.put(`/cajas/${c.id}`, { activo: !c.activo });
      toast.success(c.activo ? "Caja desactivada" : "Caja reactivada");
      loadCajas();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const exportCajas = async (fmt) => {
    try {
      const res = await api.get(`/cajas/export/${fmt}`, { params: { search: cajaSearch }, responseType: "blob" });
      downloadBlob(res.data, `cajas.${fmt}`);
    } catch {
      toast.error("No se pudo exportar");
    }
  };

  // --- movimientos ---
  const [movs, setMovs] = useState({ items: [], total: 0, counts: { todos: 0, impo: 0, expo: 0 } });
  const [movFilters, setMovFilters] = useState(() => {
    const sentidoParam = searchParams.get("sentido");
    const sentido = ["impo", "expo"].includes(sentidoParam) ? sentidoParam : sessionStorage.getItem("cajas_mov_sentido") || "all";
    return { ...EMPTY_MOV_FILTERS, sentido, caja_numero: searchParams.get("caja_numero") || "" };
  });
  const [movPage, setMovPage] = useState(0);
  const [movLoading, setMovLoading] = useState(false);
  const [movOpen, setMovOpen] = useState(false);
  const [movEditing, setMovEditing] = useState(null);
  const [movForm, setMovForm] = useState(EMPTY_MOV);
  const [movSaving, setMovSaving] = useState(false);

  const loadMovs = useCallback(async () => {
    setMovLoading(true);
    try {
      const params = { skip: movPage * limit, limit };
      Object.entries(movFilters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const { data } = await api.get("/cajas/movimientos", { params });
      setMovs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setMovLoading(false);
    }
  }, [movFilters, movPage]);

  useEffect(() => {
    if (tab !== "movimientos") return;
    const t = setTimeout(loadMovs, 250);
    return () => clearTimeout(t);
  }, [loadMovs, tab]);

  const setMovFilter = (k, v) => { setMovPage(0); setMovFilters((f) => ({ ...f, [k]: v })); };
  const setMovSentido = (v) => {
    setMovFilter("sentido", v);
    sessionStorage.setItem("cajas_mov_sentido", v);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (v === "all") p.delete("sentido"); else p.set("sentido", v); return p; });
  };
  const clearMovFilters = () => {
    setMovFilters(EMPTY_MOV_FILTERS);
    sessionStorage.removeItem("cajas_mov_sentido");
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.delete("sentido"); p.delete("caja_numero"); return p; });
  };

  const openCreateMov = () => { setMovEditing(null); setMovForm(EMPTY_MOV); setMovOpen(true); };
  const openEditMov = (m) => {
    setMovEditing(m);
    setMovForm({
      fecha: m.fecha, caja_numero: m.caja_numero, sentido: m.sentido, cliente_nombre: m.cliente_nombre || "",
      client_id: m.client_id || null, client_label: m.client_id ? m.cliente_nombre : "",
      bodega: m.bodega || "", referencia: m.referencia || "", carta_porte: m.carta_porte || "",
      carta_porte_cruce: m.carta_porte_cruce || "", factura: m.factura || "", tracto: m.tracto || "",
      operador: m.operador || "", status: m.status || "", notas: m.notas || "",
    });
    setMovOpen(true);
  };

  const saveMov = async () => {
    setMovSaving(true);
    try {
      const payload = { ...movForm };
      delete payload.client_label;
      let res;
      if (movEditing) res = await api.put(`/cajas/movimientos/${movEditing.id}`, payload);
      else res = await api.post("/cajas/movimientos", payload);
      toast.success(movEditing ? "Movimiento actualizado" : "Movimiento creado");
      if (res.data.aviso_duplicado) {
        toast.warning("Ya existía un movimiento con la misma fecha, referencia y sentido — se guardó igual");
      }
      setMovOpen(false);
      loadMovs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setMovSaving(false);
    }
  };

  const exportMovs = async (fmt) => {
    try {
      const params = {};
      Object.entries(movFilters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
      const res = await api.get(`/cajas/movimientos/export/${fmt}`, { params, responseType: "blob" });
      downloadBlob(res.data, `caja_movimientos.${fmt}`);
    } catch {
      toast.error("No se pudo exportar");
    }
  };

  const cajaTotalPages = Math.max(1, Math.ceil(cajas.total / limit));
  const movTotalPages = Math.max(1, Math.ceil(movs.total / limit));

  return (
    <div>
      <PageHeader title="Cajas" subtitle="Tus cajas: dónde está cada una y todos sus movimientos." />

      <Tabs value={tab} onValueChange={setTab} className="mt-2">
        <TabsList data-testid="cajas-tabs" data-tour="cajas-tabs-principal">
          <TabsTrigger value="catalogo" data-testid="cajas-tab-catalogo">Catálogo</TabsTrigger>
          <TabsTrigger value="movimientos" data-testid="cajas-tab-movimientos">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo" className="mt-4">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Input placeholder="Buscar número de caja" value={cajaSearch}
              onChange={(e) => { setCajaPage(0); setCajaSearch(e.target.value); }} className="sm:max-w-xs" data-testid="caja-search" data-tour="cajas-search" />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => exportCajas("csv")} className="gap-1" data-testid="cajas-export-csv">
                <Download className="h-4 w-4" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportCajas("xlsx")} className="gap-1" data-testid="cajas-export-xlsx">
                <FileSpreadsheet className="h-4 w-4" /> Excel
              </Button>
              {can("cajas", "crear") && (
                <Button onClick={openCreateCaja} className="gap-2" data-testid="new-caja-btn">
                  <Plus className="h-4 w-4" /> Nueva caja
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border border-border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead data-tour="cajas-status-column">Estado</TableHead>
                  <TableHead>Último movimiento</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cajas.items.map((c) => (
                  <TableRow key={c.id} data-testid={`caja-row-${c.numero_caja}`} className={!c.activo ? "opacity-60" : ""}>
                    <TableCell className="font-mono text-xs font-medium">{c.numero_caja}</TableCell>
                    <TableCell className="text-sm">{c.tipo || "—"}</TableCell>
                    <TableCell>
                      {c.activo
                        ? <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50">Activa</Badge>
                        : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactiva</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.ultimo_movimiento ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{c.ultimo_movimiento.fecha}</span>
                          <SentidoBadge sentido={c.ultimo_movimiento.sentido} />
                          <span className="text-muted-foreground">{c.ultimo_movimiento.cliente_nombre || ""}</span>
                        </div>
                      ) : "Sin movimientos"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {can("cajas", "editar") && (
                          <Button variant="ghost" size="icon" onClick={() => openEditCaja(c)} data-testid={`edit-caja-${c.numero_caja}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {can("cajas", "editar") && (
                          <Button variant="ghost" size="sm" onClick={() => toggleCajaActivo(c)} data-testid={`toggle-caja-${c.numero_caja}`}>
                            {c.activo ? "Desactivar" : "Reactivar"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!cajaLoading && cajas.items.length === 0 && (
                  <EmptyFiltered colSpan={5} onClear={() => setCajaSearch("")} testId="cajas-catalogo-empty" mensaje="No hay cajas con estos filtros." />
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="cajas-total-count">{cajas.total} caja(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={cajaPage === 0} onClick={() => setCajaPage((p) => p - 1)}>Anterior</Button>
              <span>Pág. {cajaPage + 1} de {cajaTotalPages}</span>
              <Button variant="outline" size="sm" disabled={cajaPage + 1 >= cajaTotalPages} onClick={() => setCajaPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="movimientos" className="mt-4">
          <div className="mb-3">
            <Tabs value={movFilters.sentido} onValueChange={setMovSentido}>
              <TabsList>
                <TabsTrigger value="all" data-testid="movs-tab-todos">Todos ({movs.counts?.todos ?? 0})</TabsTrigger>
                <TabsTrigger value="impo" data-testid="movs-tab-impo">Impo ({movs.counts?.impo ?? 0})</TabsTrigger>
                <TabsTrigger value="expo" data-testid="movs-tab-expo">Expo ({movs.counts?.expo ?? 0})</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Input placeholder="Caja" value={movFilters.caja_numero} onChange={(e) => setMovFilter("caja_numero", e.target.value)} data-testid="mov-filter-caja" />
              <Input type="date" value={movFilters.fecha_desde} onChange={(e) => setMovFilter("fecha_desde", e.target.value)} data-testid="mov-filter-fecha-desde" />
              <Input type="date" value={movFilters.fecha_hasta} onChange={(e) => setMovFilter("fecha_hasta", e.target.value)} data-testid="mov-filter-fecha-hasta" />
              <Input placeholder="Cliente" value={movFilters.cliente_nombre} onChange={(e) => setMovFilter("cliente_nombre", e.target.value)} data-testid="mov-filter-cliente" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => exportMovs("csv")} className="gap-1" data-testid="movs-export-csv">
                <Download className="h-4 w-4" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportMovs("xlsx")} className="gap-1" data-testid="movs-export-xlsx">
                <FileSpreadsheet className="h-4 w-4" /> Excel
              </Button>
              {can("cajas", "crear") && (
                <Button onClick={openCreateMov} className="gap-2" data-testid="new-mov-btn">
                  <Plus className="h-4 w-4" /> Nuevo movimiento
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border border-border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Caja</TableHead>
                  <TableHead>Sentido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Tracto</TableHead>
                  <TableHead>Operador</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movs.items.map((m) => (
                  <TableRow key={m.id} data-testid={`mov-row-${m.id}`}>
                    <TableCell className="font-mono text-xs">{m.fecha}</TableCell>
                    <TableCell className="font-mono text-xs">{m.caja_numero}</TableCell>
                    <TableCell><SentidoBadge sentido={m.sentido} /></TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm">{m.cliente_nombre || "—"}</TableCell>
                    <TableCell className="text-xs">{m.referencia || "—"}</TableCell>
                    <TableCell className="text-xs">{m.tracto || "—"}</TableCell>
                    <TableCell className="text-xs">{m.operador || "—"}</TableCell>
                    <TableCell className="text-xs">{m.status || "—"}</TableCell>
                    <TableCell className="text-right">
                      {can("cajas", "editar") && (
                        <Button variant="ghost" size="icon" onClick={() => openEditMov(m)} data-testid={`edit-mov-${m.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!movLoading && movs.items.length === 0 && (
                  <EmptyFiltered colSpan={9} onClear={clearMovFilters} testId="movs-empty" mensaje="No hay movimientos con estos filtros." />
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="movs-total-count">{movs.total} movimiento(s)</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={movPage === 0} onClick={() => setMovPage((p) => p - 1)}>Anterior</Button>
              <span>Pág. {movPage + 1} de {movTotalPages}</span>
              <Button variant="outline" size="sm" disabled={movPage + 1 >= movTotalPages} onClick={() => setMovPage((p) => p + 1)}>Siguiente</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Sheet: caja catálogo */}
      <Sheet open={cajaOpen} onOpenChange={setCajaOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-heading">{cajaEditing ? "Editar caja" : "Nueva caja"}</SheetTitle></SheetHeader>

          {cajaSimilar.length > 0 && (
            <div className="my-4 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="caja-duplicate-warning">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posible duplicado
              </div>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {cajaSimilar.map((s) => (<li key={s.id} className="font-mono text-xs">• {s.numero_caja}</li>))}
              </ul>
              <Button size="sm" className="mt-3" variant="outline" onClick={() => saveCaja(true)} data-testid="caja-force-create">
                Crear de todos modos
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4">
            <Field label="Número de caja *">
              <Input value={cajaForm.numero_caja} onChange={(e) => setCajaForm({ ...cajaForm, numero_caja: e.target.value })} data-testid="caja-form-numero" />
            </Field>
            <Field label="Tipo">
              <Input value={cajaForm.tipo} onChange={(e) => setCajaForm({ ...cajaForm, tipo: e.target.value })} data-testid="caja-form-tipo" />
            </Field>
            <Field label="Notas">
              <Textarea value={cajaForm.notas} onChange={(e) => setCajaForm({ ...cajaForm, notas: e.target.value })} data-testid="caja-form-notas" />
            </Field>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setCajaOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveCaja(false)} disabled={cajaSaving || !cajaForm.numero_caja} data-testid="caja-save-btn">
              {cajaSaving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Sheet: movimiento */}
      <Sheet open={movOpen} onOpenChange={setMovOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-heading">{movEditing ? "Editar movimiento" : "Nuevo movimiento"}</SheetTitle></SheetHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Fecha *">
              <Input type="date" value={movForm.fecha} onChange={(e) => setMovForm({ ...movForm, fecha: e.target.value })} data-testid="mov-form-fecha" />
            </Field>
            <Field label="Caja *">
              <Input value={movForm.caja_numero} onChange={(e) => setMovForm({ ...movForm, caja_numero: e.target.value })}
                placeholder="Si no existe, se crea sola" data-testid="mov-form-caja" />
            </Field>
            <Field label="Sentido *">
              <select className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm" value={movForm.sentido}
                onChange={(e) => setMovForm({ ...movForm, sentido: e.target.value })} data-testid="mov-form-sentido">
                <option value="impo">Importación</option>
                <option value="expo">Exportación</option>
              </select>
            </Field>
            <Field label="Cliente">
              <Input value={movForm.cliente_nombre} onChange={(e) => setMovForm({ ...movForm, cliente_nombre: e.target.value })} data-testid="mov-form-cliente" />
            </Field>
            <Field label="Vincular con cliente del catálogo (opcional)" className="sm:col-span-2">
              <EntityAutocomplete type="clients" value={movForm.client_id} label={movForm.client_label}
                onSelect={(c) => setMovForm({ ...movForm, client_id: c.id, client_label: `${c.codigo} — ${c.razon_social}`, cliente_nombre: movForm.cliente_nombre || c.razon_social })}
                testId="mov-form-client-link" />
            </Field>
            <Field label="Bodega"><Input value={movForm.bodega} onChange={(e) => setMovForm({ ...movForm, bodega: e.target.value })} data-testid="mov-form-bodega" /></Field>
            <Field label="Referencia"><Input value={movForm.referencia} onChange={(e) => setMovForm({ ...movForm, referencia: e.target.value })} data-testid="mov-form-referencia" /></Field>
            <Field label="Carta porte"><Input value={movForm.carta_porte} onChange={(e) => setMovForm({ ...movForm, carta_porte: e.target.value })} data-testid="mov-form-carta-porte" /></Field>
            <Field label="Carta porte cruce"><Input value={movForm.carta_porte_cruce} onChange={(e) => setMovForm({ ...movForm, carta_porte_cruce: e.target.value })} data-testid="mov-form-carta-porte-cruce" /></Field>
            <Field label="Factura"><Input value={movForm.factura} onChange={(e) => setMovForm({ ...movForm, factura: e.target.value })} data-testid="mov-form-factura" /></Field>
            <Field label="Tracto"><Input value={movForm.tracto} onChange={(e) => setMovForm({ ...movForm, tracto: e.target.value })} data-testid="mov-form-tracto" /></Field>
            <Field label="Operador"><Input value={movForm.operador} onChange={(e) => setMovForm({ ...movForm, operador: e.target.value })} data-testid="mov-form-operador" /></Field>
            <Field label="Status" className="sm:col-span-2">
              <Input value={movForm.status} onChange={(e) => setMovForm({ ...movForm, status: e.target.value })} placeholder="ENTREGADO, EN CRUCE, ..." data-testid="mov-form-status" />
            </Field>
            <Field label="Notas" className="sm:col-span-2">
              <Textarea value={movForm.notas} onChange={(e) => setMovForm({ ...movForm, notas: e.target.value })} data-testid="mov-form-notas" />
            </Field>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setMovOpen(false)}>Cancelar</Button>
            <Button onClick={saveMov} disabled={movSaving || !movForm.fecha || !movForm.caja_numero || !movForm.sentido} data-testid="mov-save-btn">
              {movSaving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function downloadBlob(blobData, filename) {
  const url = window.URL.createObjectURL(new Blob([blobData]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
